import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PaneResizeHandle } from '@/components/ui/pane-resize-handle'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import type {
  CustomerApplicationFieldValueContract,
  CustomerApplicationSourceLinkContract,
  CustomerFieldMappingTarget,
} from '@/contracts/customer-application-binding'
import { customerFieldTargetOptions } from '@/contracts/customer-application-binding'
import type {
  DuplicateCustomerSignal,
  ErpCustomerBackfillCompareContract,
  ErpCustomerBackfillField,
  ErpCustomerBackfillFieldComparison,
} from '@/contracts/customers'
import { useResizablePane } from '@/hooks/use-resizable-pane'
import {
  type CustomerApplication,
  type CustomerAuditTimelineEvent,
  type CustomerFilingStatus,
  type ManagedCustomer,
  applicationStatusMeta,
  customerFilingStatusMeta,
  getCustomerAuditTimeline,
} from '@/lib/business-workflow'
import {
  type CustomerDuplicateCandidate,
  type CustomerDuplicateComparisonField,
  buildCustomerDuplicateFieldPatch,
  createCustomerDuplicateComparisonRows,
  getCustomerDuplicateMatchLabel,
} from '@/lib/customer-duplicate-candidates'
import {
  buildErpCustomerBackfillPatchWithTargets,
  erpCustomerBackfillFieldDefinitions,
} from '@/lib/erp-customer-backfill'
import { customerDetailPaneDefaults, getTwoPaneGridTemplate } from '@/lib/resizable-panes'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { formatTaiwanAddressAnswer } from '@/lib/taiwan-address'
import { cn } from '@/lib/utils'
import {
  Archive,
  ArrowLeft,
  Building2,
  CheckSquare,
  Database,
  List,
  SearchCheck,
  UserRoundCheck,
} from 'lucide-react'
import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'

type CustomerPanel = 'management' | 'duplicates' | 'company' | 'erpFiling' | 'historyRecords'

const HISTORY_RECORD_PAGE_SIZE = 5

const panelItems: Array<{ id: CustomerPanel; label: string; icon: typeof Building2 }> = [
  { id: 'management', label: '總覽', icon: UserRoundCheck },
  { id: 'duplicates', label: '發現重複', icon: SearchCheck },
  { id: 'company', label: '客戶資訊', icon: Building2 },
  { id: 'erpFiling', label: 'ERP 建檔', icon: Database },
  { id: 'historyRecords', label: '歷程記錄', icon: List },
]

type CompanyQuestionAnswerRow = {
  id: string
  questionTitle: string
  questionType: string
  answer: string
  mappedField?: string
  managedField?: keyof ManagedCustomer
}

type CustomerDetailDialogProps = {
  customer: ManagedCustomer | null
  sourceApplication?: CustomerApplication | null
  comparisonCustomer?: ManagedCustomer | null
  sourceLink?: CustomerApplicationSourceLinkContract
  fieldValues?: CustomerApplicationFieldValueContract[]
  duplicateSignals?: DuplicateCustomerSignal[]
  duplicateCandidates?: CustomerDuplicateCandidate[]
  canEdit: boolean
  canEditFilingStatus?: boolean
  showErpFilingPanel?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onChange?: (patch: Partial<ManagedCustomer>) => void
  onCompareErpBackfill?: (input: {
    customer: ManagedCustomer
    erpCustomerCode: string
  }) => Promise<ErpCustomerBackfillCompareContract>
  onApplyDuplicateCustomerFields?: (input: {
    comparedCustomer: ManagedCustomer
    patch: Partial<ManagedCustomer>
  }) => void | Promise<void>
  onArchiveDuplicateCustomer?: (customer: ManagedCustomer) => void | Promise<void>
  onSave?: (pendingPatch?: Partial<ManagedCustomer>) => void | Promise<void>
  saveDisabled?: boolean
  saveButtonLabel?: string
  footerActions?: ReactNode
}

export function CustomerDetailDialog({
  customer,
  sourceApplication,
  comparisonCustomer,
  sourceLink,
  fieldValues = [],
  duplicateSignals = [],
  duplicateCandidates = [],
  canEdit,
  canEditFilingStatus = canEdit,
  showErpFilingPanel = false,
  open,
  onOpenChange,
  onChange,
  onCompareErpBackfill,
  onApplyDuplicateCustomerFields,
  onArchiveDuplicateCustomer,
  onSave,
  saveDisabled = false,
  saveButtonLabel = '儲存客戶資料',
  footerActions,
}: CustomerDetailDialogProps) {
  const [activePanel, setActivePanel] = useState<CustomerPanel>('management')
  const [pendingErpBackfillPatch, setPendingErpBackfillPatch] = useState<Partial<ManagedCustomer>>(
    {},
  )
  const customerId = customer?.id
  const featureVisibility = useSystemFeatureVisibility()
  const canShowErpFilingPanel = showErpFilingPanel && featureVisibility['customers.erpFiling']
  void duplicateSignals
  const duplicatePanelCount = duplicateCandidates.length
  const visiblePanelItems = useMemo(
    () =>
      panelItems.filter(
        (item) =>
          (item.id !== 'historyRecords' || featureVisibility['customers.history']) &&
          (item.id !== 'duplicates' || duplicatePanelCount > 0) &&
          (item.id !== 'erpFiling' || canShowErpFilingPanel),
      ),
    [canShowErpFilingPanel, duplicatePanelCount, featureVisibility],
  )
  const {
    width: navWidth,
    collapsed: navCollapsed,
    startResize: startNavResize,
    toggleCollapsed: toggleNavCollapsed,
  } = useResizablePane({
    initialWidth: 240,
    config: customerDetailPaneDefaults.nav,
  })
  const customerDetailGridColumns = getTwoPaneGridTemplate({
    navWidth,
    navCollapsed,
    contentMinWidth: 0,
    navConfig: customerDetailPaneDefaults.nav,
  })

  useEffect(() => {
    if (open && customerId) setActivePanel('management')
    setPendingErpBackfillPatch({})
  }, [open, customerId])

  useEffect(() => {
    if (!visiblePanelItems.some((item) => item.id === activePanel)) {
      setActivePanel(visiblePanelItems[0]?.id ?? 'management')
    }
  }, [activePanel, visiblePanelItems])

  const filingStatus = customer ? customerFilingStatusMeta[customer.filingStatus] : null
  const applicationStatus = sourceApplication
    ? applicationStatusMeta[sourceApplication.status]
    : null
  void comparisonCustomer
  const companyRows = useMemo(
    () => (customer ? buildCompanyQuestionAnswerRows(customer, fieldValues) : []),
    [customer, fieldValues],
  )
  const timelineEvents = useMemo(
    () => (customer ? getCustomerAuditTimeline(customer, sourceApplication) : []),
    [customer, sourceApplication],
  )
  const editDisabledReason = '只讀檢視：需要管理員、主管、業務或業務助理權限才能修改客戶資料。'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bottom-[10px] left-[10px] right-[10px] top-[10px] flex h-[calc(100dvh-20px)] max-h-none w-[calc(100dvw-20px)] max-w-none translate-x-0 translate-y-0 flex-col overflow-hidden p-0">
        <div className="shrink-0 border-b border-slate-200 px-6 py-5">
          <DialogHeader>
            <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <DialogTitle>{customer?.companyName ?? '客戶資料'}</DialogTitle>
                <DialogDescription className="sr-only">
                  客戶資料詳情，包含總覽、客戶資訊、ERP 建檔與歷程記錄。
                </DialogDescription>
                <div className="mt-2 flex flex-wrap gap-2">
                  {applicationStatus ? (
                    <Badge variant={applicationStatus.variant}>{applicationStatus.label}</Badge>
                  ) : null}
                  {filingStatus ? (
                    <Badge variant={filingStatus.variant}>{filingStatus.label}</Badge>
                  ) : null}
                </div>
              </div>
            </div>
          </DialogHeader>
        </div>

        {customer ? (
          <>
            <div
              data-testid="customer-detail-grid"
              className="grid min-h-0 flex-1 transition-[grid-template-columns] duration-200 ease-out lg:grid-cols-[var(--customer-detail-grid-columns)]"
              style={
                { '--customer-detail-grid-columns': customerDetailGridColumns } as CSSProperties
              }
            >
              <div className="relative min-h-0 min-w-0 overflow-hidden">
                <aside
                  className={cn(
                    'min-h-0 border-b border-slate-200 bg-slate-50 transition-opacity duration-150 lg:h-full lg:border-r lg:border-b-0',
                    navCollapsed && 'pointer-events-none opacity-0',
                  )}
                  aria-hidden={navCollapsed}
                >
                  <ScrollArea className="h-full" viewportClassName="p-3">
                    <nav
                      className="grid grid-cols-2 gap-2 lg:flex lg:flex-col"
                      role="tablist"
                      aria-label="客戶詳情區塊"
                    >
                      {visiblePanelItems.map((item) => {
                        const Icon = item.icon
                        const active = activePanel === item.id
                        return (
                          <button
                            key={item.id}
                            type="button"
                            role="tab"
                            id={`customer-detail-tab-${item.id}`}
                            aria-selected={active}
                            aria-controls={`customer-detail-panel-${item.id}`}
                            onClick={() => setActivePanel(item.id)}
                            className={cn(
                              'flex h-10 w-full items-center gap-3 rounded-md px-3 text-left text-sm font-medium text-slate-600 transition-colors hover:bg-white',
                              active && 'bg-white text-indigo-700 shadow-sm',
                            )}
                          >
                            <Icon className="size-4" />
                            {item.label}
                          </button>
                        )
                      })}
                    </nav>
                  </ScrollArea>
                </aside>
                <PaneResizeHandle
                  edge="right"
                  collapsed={navCollapsed}
                  resizeLabel="拖曳調整客戶詳情左欄寬度"
                  collapseLabel="收合客戶詳情左欄"
                  expandLabel="展開客戶詳情左欄"
                  onResizeStart={startNavResize}
                  onToggle={toggleNavCollapsed}
                />
              </div>
              <ScrollArea
                key={activePanel}
                id={`customer-detail-panel-${activePanel}`}
                role="tabpanel"
                aria-labelledby={`customer-detail-tab-${activePanel}`}
                className="min-h-0"
                viewportClassName="p-6"
              >
                {activePanel === 'management' ? (
                  <ManagementPanel
                    customer={customer}
                    sourceApplication={sourceApplication}
                    sourceLink={sourceLink}
                    canEdit={canEdit}
                    onChange={onChange}
                  />
                ) : null}
                {activePanel === 'duplicates' ? (
                  <DuplicatePanel
                    customer={customer}
                    duplicateCandidates={duplicateCandidates}
                    canEdit={canEdit}
                    onApplyDuplicateCustomerFields={onApplyDuplicateCustomerFields}
                    onArchiveDuplicateCustomer={onArchiveDuplicateCustomer}
                  />
                ) : null}
                {activePanel === 'company' ? (
                  <CompanyPanel
                    rows={companyRows}
                    canEdit={canEdit}
                    onAnswerChange={(row, nextAnswer) =>
                      onChange?.(buildCompanyQuestionAnswerPatch(customer, row, nextAnswer))
                    }
                  />
                ) : null}
                {activePanel === 'erpFiling' && canShowErpFilingPanel ? (
                  <ErpFilingPanel
                    customer={customer}
                    canEdit={canEdit}
                    canEditFilingStatus={canEditFilingStatus}
                    onChange={onChange}
                    onPendingBackfillPatchChange={setPendingErpBackfillPatch}
                    onCompareErpBackfill={onCompareErpBackfill}
                  />
                ) : null}
                {activePanel === 'historyRecords' ? (
                  <HistoryRecordsPanel events={timelineEvents} />
                ) : null}
              </ScrollArea>
            </div>
            <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-200 px-6 py-4">
              {!canEdit ? (
                <div
                  id="customer-detail-readonly-note"
                  className="mr-auto rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-600"
                >
                  {editDisabledReason}
                </div>
              ) : null}
              {footerActions}
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                關閉
              </Button>
              {onSave ? (
                <Button
                  onClick={() => onSave(pendingErpBackfillPatch)}
                  disabled={!canEdit || saveDisabled}
                  title={!canEdit ? editDisabledReason : '儲存客戶資料'}
                  aria-describedby={!canEdit ? 'customer-detail-readonly-note' : undefined}
                >
                  {saveButtonLabel}
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function ManagementPanel({
  customer,
  sourceApplication,
  sourceLink,
  canEdit,
  onChange,
}: {
  customer: ManagedCustomer
  sourceApplication?: CustomerApplication | null
  sourceLink?: CustomerApplicationSourceLinkContract
  canEdit: boolean
  onChange?: (patch: Partial<ManagedCustomer>) => void
}) {
  const sourceStatus = sourceApplication
    ? applicationStatusMeta[sourceApplication.status].label
    : '客戶主檔'

  return (
    <div className="space-y-5">
      <PanelTitle title="總覽" description="檢視來源、負責人與主管審核備註。" />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <ReadonlyInfo label="負責業務" value={`${customer.ownerName} · ${customer.ownerEmail}`} />
        <ReadonlyInfo label="主管" value={`${customer.supervisorName} · ${customer.teamName}`} />
        <ReadonlyInfo label="來源問卷" value={customer.formTitle} />
        <ReadonlyInfo label="申請狀態" value={sourceStatus} />
        <SourceRecordInfo sourceLink={sourceLink} />
        <div className="md:col-span-2 xl:col-span-3">
          <Label>內部備註</Label>
          <Textarea
            className="mt-2 min-h-28"
            value={customer.internalNotes}
            disabled={!canEdit}
            onChange={(event) => onChange?.({ internalNotes: event.currentTarget.value })}
          />
        </div>
        <div className="md:col-span-2 xl:col-span-3">
          <Label>主管審核意見</Label>
          <Textarea
            className="mt-2 min-h-28"
            value={customer.reviewNotes}
            disabled={!canEdit}
            onChange={(event) => onChange?.({ reviewNotes: event.currentTarget.value })}
          />
        </div>
      </div>
    </div>
  )
}

function DuplicatePanel({
  customer,
  duplicateCandidates,
  canEdit,
  onApplyDuplicateCustomerFields,
  onArchiveDuplicateCustomer,
}: {
  customer: ManagedCustomer
  duplicateCandidates: CustomerDuplicateCandidate[]
  canEdit: boolean
  onApplyDuplicateCustomerFields?: (input: {
    comparedCustomer: ManagedCustomer
    patch: Partial<ManagedCustomer>
  }) => void | Promise<void>
  onArchiveDuplicateCustomer?: (customer: ManagedCustomer) => void | Promise<void>
}) {
  const [selectedDuplicateCustomerId, setSelectedDuplicateCustomerId] = useState('')
  const selectedDuplicateCandidate =
    duplicateCandidates.find(
      (candidate) => candidate.customer.id === selectedDuplicateCustomerId,
    ) ?? null

  useEffect(() => {
    if (duplicateCandidates.length === 0) {
      setSelectedDuplicateCustomerId('')
      return
    }
    setSelectedDuplicateCustomerId((current) =>
      current && duplicateCandidates.some((candidate) => candidate.customer.id === current)
        ? current
        : '',
    )
  }, [duplicateCandidates])

  if (duplicateCandidates.length > 0) {
    return (
      <div className="space-y-5">
        {selectedDuplicateCandidate ? (
          <CustomerDuplicateComparisonPanel
            customer={customer}
            comparedCustomer={selectedDuplicateCandidate.customer}
            canEdit={canEdit}
            onApplyDuplicateCustomerFields={onApplyDuplicateCustomerFields}
            onBack={() => setSelectedDuplicateCustomerId('')}
          />
        ) : (
          <DuplicateCandidateList
            candidates={duplicateCandidates}
            archivingEnabled={Boolean(onArchiveDuplicateCustomer)}
            onCompare={(candidate) => setSelectedDuplicateCustomerId(candidate.customer.id)}
            onArchive={onArchiveDuplicateCustomer}
          />
        )}
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
      目前沒有符合條件的未封存重複客戶資料。
    </div>
  )
}

function DuplicateCandidateList({
  candidates,
  archivingEnabled,
  onCompare,
  onArchive,
}: {
  candidates: CustomerDuplicateCandidate[]
  archivingEnabled: boolean
  onCompare: (candidate: CustomerDuplicateCandidate) => void
  onArchive?: (customer: ManagedCustomer) => void | Promise<void>
}) {
  const [archivingCustomerId, setArchivingCustomerId] = useState('')
  const [archiveMessage, setArchiveMessage] = useState('')

  async function archiveCandidate(candidate: CustomerDuplicateCandidate) {
    if (!onArchive) return
    setArchivingCustomerId(candidate.customer.id)
    setArchiveMessage('')
    try {
      await onArchive(candidate.customer)
      setArchiveMessage('已封存所選比對客戶資料。')
    } catch (error) {
      setArchiveMessage(error instanceof Error ? error.message : '封存比對客戶資料失敗。')
    } finally {
      setArchivingCustomerId('')
    }
  }

  return (
    <div className="overflow-hidden rounded-lg border border-amber-200 bg-white shadow-sm">
      <div className="border-b border-amber-200 bg-amber-50 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-semibold text-amber-950">偵測到可能重複客戶</h3>
            <p className="mt-1 text-sm text-amber-800">
              下列客戶資料與本身客戶資料的客戶簡稱、統一編號或既有客戶代號相同。
            </p>
          </div>
          <Badge variant="warning">{candidates.length} 筆</Badge>
        </div>
      </div>
      {archiveMessage ? (
        <div className="mx-4 mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {archiveMessage}
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <Table aria-label="偵測到可能重複客戶">
          <TableHeader>
            <TableRow>
              <TableHead>客戶資料</TableHead>
              <TableHead>重複依據</TableHead>
              <TableHead>統一編號</TableHead>
              <TableHead>負責業務</TableHead>
              <TableHead className="text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {candidates.map((candidate) => (
              <TableRow key={candidate.customer.id} className="align-top">
                <TableCell className="min-w-[220px]">
                  <div className="font-semibold text-slate-950">
                    {candidate.customer.companyName || '未命名客戶'}
                  </div>
                  {candidate.customer.erpCustomerCode ? (
                    <Badge className="mt-1" variant="outline">
                      {candidate.customer.erpCustomerCode}
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell className="min-w-[180px]">
                  <div className="flex flex-wrap gap-1.5">
                    {candidate.matchedFields.map((field) => (
                      <Badge key={field} variant="warning">
                        {getCustomerDuplicateMatchLabel(field)}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="min-w-[120px] text-slate-700">
                  {candidate.customer.taxId || '-'}
                </TableCell>
                <TableCell className="min-w-[140px] text-slate-700">
                  {candidate.customer.ownerName || '-'}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button type="button" size="sm" onClick={() => onCompare(candidate)}>
                      比對資料
                    </Button>
                    {archivingEnabled ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={archivingCustomerId === candidate.customer.id}
                        onClick={() => void archiveCandidate(candidate)}
                      >
                        <Archive className="size-4" />
                        {archivingCustomerId === candidate.customer.id ? '封存中' : '封存客戶資料'}
                      </Button>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function CustomerDuplicateComparisonPanel({
  customer,
  comparedCustomer,
  canEdit,
  onApplyDuplicateCustomerFields,
  onBack,
}: {
  customer: ManagedCustomer
  comparedCustomer: ManagedCustomer
  canEdit: boolean
  onApplyDuplicateCustomerFields?: (input: {
    comparedCustomer: ManagedCustomer
    patch: Partial<ManagedCustomer>
  }) => void | Promise<void>
  onBack: () => void
}) {
  const rows = useMemo(
    () => createCustomerDuplicateComparisonRows(customer, comparedCustomer),
    [comparedCustomer, customer],
  )
  const selectableFields = rows.filter((row) => row.selectable).map((row) => row.field)
  const [selectedFields, setSelectedFields] = useState<CustomerDuplicateComparisonField[]>([])
  const [applyStatus, setApplyStatus] = useState<'idle' | 'saving'>('idle')
  const [applyMessage, setApplyMessage] = useState('')
  const allSelectableChecked =
    selectableFields.length > 0 && selectableFields.every((field) => selectedFields.includes(field))
  const duplicateComparisonResetKey = `${customer.id}|${comparedCustomer.id}`

  useEffect(() => {
    void duplicateComparisonResetKey
    setSelectedFields([])
    setApplyStatus('idle')
    setApplyMessage('')
  }, [duplicateComparisonResetKey])

  function toggleField(field: CustomerDuplicateComparisonField) {
    setSelectedFields((current) =>
      current.includes(field) ? current.filter((item) => item !== field) : [...current, field],
    )
  }

  function toggleAllFields() {
    setSelectedFields(allSelectableChecked ? [] : selectableFields)
  }

  async function applySelectedFields() {
    if (!onApplyDuplicateCustomerFields || selectedFields.length === 0) return
    setApplyStatus('saving')
    setApplyMessage('')
    try {
      const patch = buildCustomerDuplicateFieldPatch(customer, comparedCustomer, selectedFields)
      await onApplyDuplicateCustomerFields({ comparedCustomer, patch })
      setSelectedFields([])
      setApplyMessage('已更新所選比對資料，客戶資料已重新讀取。')
    } catch (error) {
      setApplyMessage(error instanceof Error ? error.message : '更新所選比對資料失敗。')
    } finally {
      setApplyStatus('idle')
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onBack}>
              <ArrowLeft className="size-4" />
              返回清單
            </Button>
            <h3 className="font-semibold text-slate-950">欄位對照</h3>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            與「{comparedCustomer.companyName || '未命名客戶'}」比對，可勾選欄位更新本身客戶資料。
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={toggleAllFields}>
            {allSelectableChecked ? '取消全部勾選' : '全部勾選'}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={
              !canEdit ||
              !onApplyDuplicateCustomerFields ||
              selectedFields.length === 0 ||
              applyStatus === 'saving'
            }
            onClick={() => void applySelectedFields()}
          >
            <CheckSquare className="size-4" />
            {applyStatus === 'saving' ? '更新中' : '更新為所選比對資料'}
          </Button>
        </div>
      </div>
      {applyMessage ? (
        <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          {applyMessage}
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <Table aria-label="欄位對照">
          <TableHeader>
            <TableRow>
              <TableHead>勾選</TableHead>
              <TableHead>欄位</TableHead>
              <TableHead>客戶資料</TableHead>
              <TableHead>比對資料</TableHead>
              <TableHead>狀態</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.field}>
                <TableCell>
                  <input
                    type="checkbox"
                    aria-label={`套用 ${row.label}`}
                    checked={selectedFields.includes(row.field)}
                    disabled={!row.selectable}
                    onChange={() => toggleField(row.field)}
                    className="size-4 rounded border-slate-300 text-[#4a2f21]"
                  />
                </TableCell>
                <TableCell className="min-w-[140px] font-medium text-slate-900">
                  {row.label}
                </TableCell>
                <TableCell className="min-w-[180px] max-w-[320px] whitespace-normal break-words">
                  {row.customerValue || '-'}
                </TableCell>
                <TableCell className="min-w-[180px] max-w-[320px] whitespace-normal break-words text-slate-600">
                  {row.comparedValue || '-'}
                </TableCell>
                <TableCell>
                  <Badge variant={row.status === 'same' ? 'secondary' : 'warning'}>
                    {row.statusLabel}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function ErpFilingPanel({
  customer,
  canEdit,
  canEditFilingStatus,
  onChange,
  onPendingBackfillPatchChange,
  onCompareErpBackfill,
}: {
  customer: ManagedCustomer
  canEdit: boolean
  canEditFilingStatus: boolean
  onChange?: (patch: Partial<ManagedCustomer>) => void
  onPendingBackfillPatchChange?: (patch: Partial<ManagedCustomer>) => void
  onCompareErpBackfill?: (input: {
    customer: ManagedCustomer
    erpCustomerCode: string
  }) => Promise<ErpCustomerBackfillCompareContract>
}) {
  const [erpCompareResult, setErpCompareResult] =
    useState<ErpCustomerBackfillCompareContract | null>(null)
  const [erpCompareSelectedFields, setErpCompareSelectedFields] = useState<
    ErpCustomerBackfillField[]
  >([])
  const [erpCompareFieldTargets, setErpCompareFieldTargets] = useState<
    Partial<Record<ErpCustomerBackfillField, ErpCustomerBackfillField>>
  >({})
  const [erpCompareStatus, setErpCompareStatus] = useState<'idle' | 'loading'>('idle')
  const [erpCompareMessage, setErpCompareMessage] = useState('')
  const erpCompareResetKey = `${customer.id}|${customer.erpCustomerCode}`
  const selectableErpFields = useMemo(
    () => erpCompareResult?.fields.filter((row) => row.selectable).map((row) => row.field) ?? [],
    [erpCompareResult],
  )

  useEffect(() => {
    void erpCompareResetKey
    setErpCompareResult(null)
    setErpCompareSelectedFields([])
    setErpCompareFieldTargets({})
    setErpCompareStatus('idle')
    setErpCompareMessage('')
    onPendingBackfillPatchChange?.({})
  }, [erpCompareResetKey, onPendingBackfillPatchChange])

  async function compareErpBackfill() {
    if (!onCompareErpBackfill) {
      setErpCompareMessage('ERP 客戶資料查詢 API 尚未啟用。')
      return
    }
    const erpCustomerCode = customer.erpCustomerCode.trim()
    if (!erpCustomerCode) {
      setErpCompareMessage('請先輸入客戶代號。')
      return
    }
    setErpCompareStatus('loading')
    setErpCompareMessage('')
    try {
      const result = await onCompareErpBackfill({ customer, erpCustomerCode })
      setErpCompareResult(result)
      setErpCompareSelectedFields([])
      setErpCompareFieldTargets({})
      onPendingBackfillPatchChange?.({})
      setErpCompareMessage(result.message)
    } catch (error) {
      setErpCompareResult(null)
      setErpCompareSelectedFields([])
      setErpCompareFieldTargets({})
      onPendingBackfillPatchChange?.({})
      setErpCompareMessage(error instanceof Error ? error.message : 'ERP 客戶資料比對失敗。')
    } finally {
      setErpCompareStatus('idle')
    }
  }

  function toggleErpCompareField(field: ErpCustomerBackfillField) {
    if (!erpCompareResult) return
    const nextSelectedFields = erpCompareSelectedFields.includes(field)
      ? erpCompareSelectedFields.filter((item) => item !== field)
      : [...erpCompareSelectedFields, field]
    const nextTargets = {
      ...erpCompareFieldTargets,
      [field]: erpCompareFieldTargets[field] ?? field,
    }
    setErpCompareSelectedFields(nextSelectedFields)
    setErpCompareFieldTargets(nextTargets)
    publishPendingBackfillPatch(erpCompareResult, nextSelectedFields, nextTargets)
  }

  function updateErpCompareFieldTarget(
    field: ErpCustomerBackfillField,
    targetField: ErpCustomerBackfillField,
  ) {
    if (!erpCompareResult) return
    const nextTargets = {
      ...erpCompareFieldTargets,
      [field]: targetField,
    }
    const nextSelectedFields = erpCompareSelectedFields.includes(field)
      ? erpCompareSelectedFields
      : [...erpCompareSelectedFields, field]
    setErpCompareFieldTargets(nextTargets)
    setErpCompareSelectedFields(nextSelectedFields)
    publishPendingBackfillPatch(erpCompareResult, nextSelectedFields, nextTargets)
  }

  function selectAllErpFields() {
    if (!erpCompareResult) return
    const nextTargets = { ...erpCompareFieldTargets }
    for (const field of selectableErpFields) {
      nextTargets[field] = nextTargets[field] ?? field
    }
    setErpCompareSelectedFields(selectableErpFields)
    setErpCompareFieldTargets(nextTargets)
    publishPendingBackfillPatch(erpCompareResult, selectableErpFields, nextTargets)
  }

  function clearSelectedErpFields() {
    setErpCompareSelectedFields([])
    onPendingBackfillPatchChange?.({})
    setErpCompareMessage('已取消選取欄位。')
  }

  function publishPendingBackfillPatch(
    result: ErpCustomerBackfillCompareContract,
    selectedFields: ErpCustomerBackfillField[],
    fieldTargets: Partial<Record<ErpCustomerBackfillField, ErpCustomerBackfillField>>,
  ) {
    const patch = buildErpCustomerBackfillPatchWithTargets(
      result.fields,
      selectedFields,
      fieldTargets,
    )
    onPendingBackfillPatchChange?.(patch)
    if (Object.keys(patch).length === 0) {
      setErpCompareMessage('沒有已選取且可寫回的 ERP 欄位。')
      return
    }
    setErpCompareMessage(
      `已選取 ${Object.keys(patch).length} 個欄位，按「儲存客戶資料」寫回資料庫。`,
    )
  }

  return (
    <div className="space-y-5">
      <PanelTitle
        title="ERP 建檔"
        description="回填客戶代號，並比對 ERP 客戶資料與目前問卷資料。"
      />
      <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
        <div className="mb-3">
          <h3 className="font-semibold text-indigo-950">ERP 建檔回填資訊</h3>
          <p className="mt-1 text-sm text-indigo-700">
            比對後可選擇 ERP 欄位要更新到哪一個問卷欄位，再按「儲存客戶資料」寫回。
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <Field
            label="客戶代號"
            value={customer.erpCustomerCode}
            disabled={!canEdit}
            onChange={(erpCustomerCode) => onChange?.({ erpCustomerCode })}
          />
          <div className="flex items-end">
            <Button
              type="button"
              variant="outline"
              className="w-full border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-100"
              aria-label="比對 ERP 客戶資料"
              disabled={
                !canEdit ||
                !customer.erpCustomerCode.trim() ||
                erpCompareStatus === 'loading' ||
                !onCompareErpBackfill
              }
              onClick={compareErpBackfill}
            >
              <SearchCheck className="size-4" />
              {erpCompareStatus === 'loading' ? '比對中' : '比對'}
            </Button>
          </div>
          <Field
            label="ERP 建檔日期"
            type="date"
            value={customer.erpFiledAt}
            disabled={!canEdit}
            onChange={(erpFiledAt) => onChange?.({ erpFiledAt })}
          />
          <SelectField
            label="建檔進度"
            value={customer.filingStatus}
            disabled={!canEditFilingStatus}
            options={[
              { value: 'not_filed', label: 'ERP未建檔' },
              { value: 'filed', label: 'ERP已建檔' },
            ]}
            onChange={(filingStatus) =>
              onChange?.({ filingStatus: filingStatus as CustomerFilingStatus })
            }
          />
          <Field
            label="建檔人"
            value={customer.erpFiledBy}
            disabled={!canEdit}
            onChange={(erpFiledBy) => onChange?.({ erpFiledBy })}
          />
        </div>
        {erpCompareMessage ? (
          <output className="mt-3 rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm text-indigo-900">
            {erpCompareMessage}
          </output>
        ) : null}
        {erpCompareResult ? (
          <ErpBackfillComparisonPanel
            rows={erpCompareResult.fields}
            selectedFields={erpCompareSelectedFields}
            fieldTargets={erpCompareFieldTargets}
            canEdit={canEdit}
            onToggleField={toggleErpCompareField}
            onTargetFieldChange={updateErpCompareFieldTarget}
            onSelectAllFields={selectAllErpFields}
            onClearSelectedFields={clearSelectedErpFields}
          />
        ) : null}
      </div>
    </div>
  )
}

const erpBackfillStateMeta: Record<
  ErpCustomerBackfillFieldComparison['state'],
  { label: string; variant: 'success' | 'secondary' | 'warning' }
> = {
  new: { label: '新增', variant: 'success' },
  changed: { label: '不同', variant: 'warning' },
  same: { label: '相同', variant: 'secondary' },
  empty: { label: 'ERP 空值', variant: 'secondary' },
}

function ErpBackfillComparisonPanel({
  rows,
  selectedFields,
  fieldTargets,
  canEdit,
  onToggleField,
  onTargetFieldChange,
  onSelectAllFields,
  onClearSelectedFields,
}: {
  rows: ErpCustomerBackfillFieldComparison[]
  selectedFields: ErpCustomerBackfillField[]
  fieldTargets: Partial<Record<ErpCustomerBackfillField, ErpCustomerBackfillField>>
  canEdit: boolean
  onToggleField: (field: ErpCustomerBackfillField) => void
  onTargetFieldChange: (
    field: ErpCustomerBackfillField,
    targetField: ErpCustomerBackfillField,
  ) => void
  onSelectAllFields: () => void
  onClearSelectedFields: () => void
}) {
  const visibleRows = rows.filter((row) => row.state !== 'empty')
  const selectedCount = selectedFields.length
  const selectableCount = visibleRows.filter((row) => row.selectable).length
  const allSelectableSelected = selectableCount > 0 && selectedCount >= selectableCount
  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-indigo-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-indigo-100 px-4 py-3">
        <div>
          <h4 className="font-semibold text-slate-950">ERP 客戶資料比對</h4>
          <p className="mt-1 text-sm text-slate-500">
            選取要套用的 ERP 資料；若對應不準，可改選要寫回的問卷欄位。
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={allSelectableSelected ? onClearSelectedFields : onSelectAllFields}
            disabled={!canEdit || selectableCount === 0}
          >
            <CheckSquare className="size-4" />
            {allSelectableSelected ? '取消全選' : '全部勾選'}
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table aria-label="ERP 客戶資料比對清單">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[64px]">套用</TableHead>
              <TableHead>ERP 欄位</TableHead>
              <TableHead>套用到問卷欄位</TableHead>
              <TableHead>目前題目回答</TableHead>
              <TableHead>ERP 資料</TableHead>
              <TableHead>狀態</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.length > 0 ? (
              visibleRows.map((row) => {
                const meta = erpBackfillStateMeta[row.state]
                return (
                  <TableRow key={row.field}>
                    <TableCell>
                      <input
                        type="checkbox"
                        className="size-4 rounded border-slate-300 text-indigo-600"
                        aria-label={`選取 ${row.label}`}
                        checked={selectedFields.includes(row.field)}
                        disabled={!row.selectable || !canEdit}
                        onChange={() => onToggleField(row.field)}
                      />
                    </TableCell>
                    <TableCell className="min-w-[140px] font-medium text-slate-900">
                      {row.label}
                    </TableCell>
                    <TableCell className="min-w-[180px]">
                      <Select
                        value={fieldTargets[row.field] ?? row.field}
                        onValueChange={(value) =>
                          onTargetFieldChange(row.field, value as ErpCustomerBackfillField)
                        }
                        disabled={!row.selectable || !canEdit}
                      >
                        <SelectTrigger aria-label={`${row.label} 套用到問卷欄位`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {erpCustomerBackfillFieldDefinitions.map((definition) => (
                            <SelectItem key={definition.field} value={definition.field}>
                              {definition.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="min-w-[180px] max-w-[320px] whitespace-normal break-words text-slate-600">
                      {row.currentValue || '-'}
                    </TableCell>
                    <TableCell className="min-w-[180px] max-w-[320px] whitespace-normal break-words text-slate-950">
                      {row.erpValue || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={meta.variant}>{meta.label}</Badge>
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="py-6 text-center text-sm text-slate-500">
                  ERP 回應沒有可顯示的欄位差異。
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

const customerFieldTargetLabelByValue = new Map(
  customerFieldTargetOptions.map((option) => [option.value, option]),
)

const managedCustomerFieldByQuestionTarget: Partial<
  Record<CustomerFieldMappingTarget, keyof ManagedCustomer>
> = {
  existingCustomerCode: 'erpCustomerCode',
  customerShortName: 'companyName',
  companyName: 'companyName',
  taxId: 'taxId',
  customerChannel: 'industry',
  industry: 'industry',
  customerType: 'customerType',
  department: 'departmentName',
  companySize: 'companySize',
  employeeCountRange: 'companySize',
  website: 'website',
  deliveryAddress: 'companyAddress',
  companyAddress: 'companyAddress',
  phone1: 'companyPhone',
  companyPhone: 'companyPhone',
  phone2: 'contactPhone',
  contactPhone: 'contactPhone',
  email: 'contactEmail',
  contactEmail: 'contactEmail',
  contactName: 'contactName',
  contactTitle: 'contactTitle',
  paymentTerms: 'paymentTerms',
  salesRegion: 'salesRegion',
  expectedValue: 'expectedValue',
  needSummary: 'needSummary',
  internalNotes: 'internalNotes',
  ownerEmail: 'ownerEmail',
}

function buildCompanyQuestionAnswerRows(
  customer: ManagedCustomer,
  fieldValues: CustomerApplicationFieldValueContract[],
): CompanyQuestionAnswerRow[] {
  const rows: CompanyQuestionAnswerRow[] = []
  const rowIndexByQuestionTitle = new Map<string, number>()

  function upsert(row: CompanyQuestionAnswerRow) {
    const key = normalizeQuestionKey(row.questionTitle || row.id)
    const existingIndex = rowIndexByQuestionTitle.get(key)
    if (existingIndex === undefined) {
      rowIndexByQuestionTitle.set(key, rows.length)
      rows.push(row)
      return
    }
    rows[existingIndex] = row
  }

  for (const answer of customer.answers) {
    upsert({
      id: answer.id,
      questionTitle: answer.questionTitle,
      questionType: answer.questionType,
      answer:
        getManagedCustomerQuestionValue(customer, answer.mappedField) ??
        normalizeCompanyAnswerValue(answer.answer),
      mappedField: answer.mappedField,
      managedField: getManagedCustomerFieldByQuestionTarget(answer.mappedField),
    })
  }

  for (const fieldValue of fieldValues) {
    const questionTitle =
      fieldValue.sourceQuestionTitle ??
      fieldValue.label ??
      customerFieldTargetLabelByValue.get(fieldValue.fieldKey)?.shortLabel ??
      fieldValue.fieldKey
    const fieldKey = normalizeQuestionKey(questionTitle || fieldValue.fieldKey)
    upsert({
      id: fieldValue.sourceQuestionId ?? fieldValue.sourceAnswerId ?? fieldValue.fieldKey,
      questionTitle,
      questionType: 'SHORT_TEXT',
      answer:
        getManagedCustomerQuestionValue(customer, fieldValue.fieldKey) ??
        normalizeCompanyAnswerValue(fieldValue.value),
      mappedField: fieldValue.fieldKey,
      managedField: getManagedCustomerFieldByQuestionTarget(fieldValue.fieldKey),
    })
  }

  return rows.filter((row) => row.questionTitle.trim() !== '')
}

function getManagedCustomerQuestionValue(customer: ManagedCustomer, target: string | undefined) {
  if (!target || !isCustomerFieldMappingTarget(target)) return undefined
  const managedField = getManagedCustomerFieldByQuestionTarget(target)
  if (!managedField) return undefined
  const value = customer[managedField]
  const normalized = normalizeCompanyAnswerValue(value)
  return normalized || undefined
}

function getManagedCustomerFieldByQuestionTarget(target: string | undefined) {
  if (!target || !isCustomerFieldMappingTarget(target)) return undefined
  return managedCustomerFieldByQuestionTarget[target]
}

function isCustomerFieldMappingTarget(value: string): value is CustomerFieldMappingTarget {
  return customerFieldTargetLabelByValue.has(value as CustomerFieldMappingTarget)
}

function normalizeQuestionKey(value: string) {
  return value.trim().toLocaleLowerCase()
}

function normalizeCompanyAnswerValue(value: unknown) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return Number.isFinite(value) && value !== 0 ? String(value) : ''
  const formattedAddress = formatTaiwanAddressAnswer(value)
  if (formattedAddress) return formattedAddress
  return String(value).trim()
}

function buildCompanyQuestionAnswerPatch(
  customer: ManagedCustomer,
  row: CompanyQuestionAnswerRow,
  nextAnswer: string,
): Partial<ManagedCustomer> {
  const normalizedAnswer = nextAnswer.trim()
  const patch: Partial<ManagedCustomer> = {
    answers: upsertCustomerQuestionAnswer(customer.answers, row, normalizedAnswer),
  }

  if (row.managedField) {
    assignManagedCustomerField(patch, row.managedField, normalizedAnswer)
  }

  return patch
}

function upsertCustomerQuestionAnswer(
  answers: ManagedCustomer['answers'],
  row: CompanyQuestionAnswerRow,
  nextAnswer: string,
) {
  const rowTitleKey = normalizeQuestionKey(row.questionTitle)
  const rowId = row.id
  const existingIndex = answers.findIndex(
    (answer) =>
      answer.id === rowId ||
      normalizeQuestionKey(answer.questionTitle) === rowTitleKey ||
      (row.mappedField && answer.mappedField === row.mappedField),
  )
  const nextItem = {
    id: rowId,
    questionTitle: row.questionTitle,
    questionType: row.questionType || 'SHORT_TEXT',
    answer: nextAnswer,
    mappedField: row.mappedField ?? '',
  }

  if (existingIndex < 0) return [...answers, nextItem]
  return answers.map((answer, index) =>
    index === existingIndex ? { ...answer, ...nextItem } : answer,
  )
}

function assignManagedCustomerField(
  patch: Partial<ManagedCustomer>,
  field: keyof ManagedCustomer,
  value: string,
) {
  if (field === 'creditLimit' || field === 'expectedValue') {
    ;(patch as Record<string, unknown>)[field] = Number(value.replace(/,/g, '')) || 0
    return
  }
  ;(patch as Record<string, unknown>)[field] = value
}

function CompanyPanel({
  rows,
  canEdit,
  onAnswerChange,
}: {
  rows: CompanyQuestionAnswerRow[]
  canEdit: boolean
  onAnswerChange: (row: CompanyQuestionAnswerRow, nextAnswer: string) => void
}) {
  return (
    <div className="space-y-5">
      <PanelTitle title="客戶資訊" description="客戶資訊以最後更新的問卷題目與題目回答為主。" />
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <Table aria-label="客戶資訊問卷題目回答">
            <TableHeader>
              <TableRow>
                <TableHead>問卷題目</TableHead>
                <TableHead>題目回答</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="min-w-[220px] font-medium text-slate-900">
                      {row.questionTitle}
                    </TableCell>
                    <TableCell className="min-w-[260px] whitespace-pre-wrap text-slate-700">
                      {canEdit ? (
                        <Textarea
                          aria-label={`編輯 ${row.questionTitle}`}
                          className="min-h-16 resize-y"
                          value={row.answer}
                          onChange={(event) => onAnswerChange(row, event.currentTarget.value)}
                        />
                      ) : (
                        row.answer || '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} className="py-6 text-center text-sm text-slate-500">
                    這筆客戶目前沒有可追溯的問卷題目回答。
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}

function SourceRecordInfo({
  sourceLink,
}: {
  sourceLink?: CustomerApplicationSourceLinkContract
}) {
  if (!sourceLink) {
    return <ReadonlyInfo label="來源記錄" value="手動建立，沒有指定問卷記錄" />
  }

  const recordId = sourceLink.responseId || sourceLink.sourceRecordId.replace(/^source-/, '')
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-xs text-slate-500">來源記錄</div>
      <div className="mt-1 break-words font-medium text-slate-900">
        填寫時間：{sourceLink.createdAt || '-'}
      </div>
      <div className="mt-1 break-words text-xs text-slate-500">填寫記錄ID：{recordId || '-'}</div>
    </div>
  )
}

const historyToneLabel: Record<CustomerAuditTimelineEvent['tone'], string> = {
  neutral: '記錄',
  success: '完成',
  warning: '待處理',
  danger: '已拒絕',
}

function HistoryRecordsPanel({ events }: { events: CustomerAuditTimelineEvent[] }) {
  const [currentPage, setCurrentPage] = useState(1)
  const pageCount = Math.max(1, Math.ceil(events.length / HISTORY_RECORD_PAGE_SIZE))
  const pageEvents = useMemo(() => {
    const start = (currentPage - 1) * HISTORY_RECORD_PAGE_SIZE
    return events.slice(start, start + HISTORY_RECORD_PAGE_SIZE)
  }, [currentPage, events])

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, pageCount))
  }, [pageCount])

  return (
    <div className="space-y-5">
      <PanelTitle title="歷程記錄" description="清單檢視建檔與審核歷史。" />
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        {events.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <Table aria-label="客戶歷程記錄清單">
                <TableHeader>
                  <TableRow>
                    <TableHead>時間</TableHead>
                    <TableHead>項目</TableHead>
                    <TableHead>處理人</TableHead>
                    <TableHead>狀態</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageEvents.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell className="min-w-[150px] text-slate-600">
                        {event.at || '-'}
                      </TableCell>
                      <TableCell className="min-w-[220px] font-medium text-slate-950">
                        {event.label}
                      </TableCell>
                      <TableCell className="min-w-[140px] text-slate-600">
                        {event.actor || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            event.tone === 'warning'
                              ? 'warning'
                              : event.tone === 'danger'
                                ? 'destructive'
                                : 'outline'
                          }
                        >
                          {historyToneLabel[event.tone]}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm text-slate-500">
              <span>
                每頁 {HISTORY_RECORD_PAGE_SIZE} 筆，共 {events.length} 筆，第 {currentPage} /{' '}
                {pageCount} 頁
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                >
                  上一頁
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= pageCount}
                  onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                >
                  下一頁
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="p-6 text-sm text-slate-500">目前沒有歷程記錄。</div>
        )}
      </div>
    </div>
  )
}

function PanelTitle({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
      <p className="mt-1 text-sm text-slate-500">{description}</p>
    </div>
  )
}

function ReadonlyInfo({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 break-words font-medium text-slate-900">{value || '-'}</div>
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  disabled,
  type = 'text',
}: {
  label: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  type?: string
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    </div>
  )
}

function SelectField({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
  disabled?: boolean
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
